import type { Box, Frame } from "./frame";

/**
 * Anivaze v4 — the CAMERA / scroll system (PLATFORM-ADDITIONS §5).
 *
 * A scene may render a "world" larger than the frame (a tall flow diagram, a
 * wide architecture chart, a Ken-Burns still) and hand the Stage a `CameraTrack`
 * — keyframes of {center, zoom} in WORLD coordinates, sampled as a pure function
 * of `t` (seconds within the beat). The Stage turns the sampled camera into one
 * SVG transform that maps the world into the frame. Deterministic → preview ===
 * export. When a scene declares no camera, the view is identity (no regression).
 *
 * This is the primitive the flexible flow/architecture actor rides (§6): open
 * zoomed-out on the whole graph, then scroll/pan to each section as it's narrated.
 */

export interface CameraKey {
  /** Seconds within the beat. */
  t: number;
  /** World point placed at the frame centre. */
  cx: number;
  cy: number;
  /** >1 zooms in, <1 zooms out. 1 = world unit === frame unit. */
  zoom: number;
}

export type CameraTrack = CameraKey[];

/** smoothstep — eases camera moves so scrolls glide instead of snapping. */
const smooth = (a: number): number => {
  const x = a < 0 ? 0 : a > 1 ? 1 : a;
  return x * x * (3 - 2 * x);
};

const IDENTITY = { cx: 0, cy: 0, zoom: 1 };

/** Sample the camera at time `t` (seconds), eased between keyframes. */
export function cameraAt(track: CameraTrack, t: number): { cx: number; cy: number; zoom: number } {
  if (!track || track.length === 0) return { ...IDENTITY };
  if (track.length === 1 || t <= track[0].t) return pick(track[0]);
  const last = track[track.length - 1];
  if (t >= last.t) return pick(last);
  for (let i = 0; i < track.length - 1; i++) {
    const a = track[i];
    const b = track[i + 1];
    if (t >= a.t && t <= b.t) {
      const u = b.t === a.t ? 1 : smooth((t - a.t) / (b.t - a.t));
      return {
        cx: a.cx + (b.cx - a.cx) * u,
        cy: a.cy + (b.cy - a.cy) * u,
        zoom: a.zoom + (b.zoom - a.zoom) * u,
      };
    }
  }
  return pick(last);
}

const pick = (k: CameraKey) => ({ cx: k.cx, cy: k.cy, zoom: k.zoom });

/**
 * The SVG transform that maps the world into the frame so the camera's
 * (cx, cy) sits at the frame centre at the given zoom. Applied to the content
 * group in Stage. Identity when cam = {0,0,1} would NOT centre correctly, so
 * callers pass real world centres; the "no camera" case skips this entirely.
 */
export function cameraTransform(cam: { cx: number; cy: number; zoom: number }, frame: Frame): string {
  return cameraTransformCentered(cam, frame.w / 2, frame.h / 2);
}

/**
 * Same mapping, but centred on an ARBITRARY point — so an actor can scroll its
 * own content INSIDE its visual box (world → box centre) while the beat's
 * headline/captions stay fixed. `cameraTransform` is this centred on the frame.
 */
export function cameraTransformCentered(
  cam: { cx: number; cy: number; zoom: number },
  centerX: number,
  centerY: number,
): string {
  return `translate(${round(centerX)} ${round(centerY)}) scale(${round(cam.zoom, 4)}) translate(${round(-cam.cx)} ${round(-cam.cy)})`;
}

/** A viewport is anything with a width and height — a Frame or an actor's box. */
export interface Viewport {
  w: number;
  h: number;
}

const round = (n: number, p = 2): number => {
  const f = 10 ** p;
  return Math.round(n * f) / f;
};

/** Zoom that fits `box` inside `view` with `pad` breathing room (pad>1 = looser). */
export function fitZoom(box: Box, view: Viewport, pad = 1.12): number {
  const zx = view.w / (box.w * pad);
  const zy = view.h / (box.h * pad);
  return Math.min(zx, zy);
}

/** A single camera pose that centres `box` and fits it to the `view`. */
export function focusBox(box: Box, view: Viewport, pad = 1.12): { cx: number; cy: number; zoom: number } {
  return { cx: box.cx, cy: box.cy, zoom: fitZoom(box, view, pad) };
}

/**
 * The default "walk": hold an OVERVIEW of `world` for `introFrac` of the beat,
 * then ease in to FOCUS on `target`, holding it to the end. This is the motion
 * the stakeholder described — full flow zoomed out, then scroll to the section
 * being talked about.
 */
export function overviewThenFocus(
  world: Box,
  target: Box,
  view: Viewport,
  dur: number,
  opts: { introFrac?: number; overviewPad?: number; focusPad?: number } = {},
): CameraTrack {
  const introFrac = opts.introFrac ?? 0.28;
  const overview = focusBox(world, view, opts.overviewPad ?? 1.06);
  const focus = focusBox(target, view, opts.focusPad ?? 1.15);
  const hold = Math.max(0.2, dur * introFrac);
  const settle = Math.min(dur - 0.05, hold + Math.max(0.6, dur * 0.45));
  return [
    { t: 0, ...overview },
    { t: hold, ...overview },
    { t: settle, ...focus },
    { t: dur, ...focus },
  ];
}

/**
 * Scroll a tall (or wide) `world` steadily past the frame — for long lists /
 * deep flows where there's no single focus, just a continuous read.
 */
export function scrollWorld(
  world: Box,
  view: Viewport,
  dur: number,
  dir: "down" | "right" = "down",
): CameraTrack {
  const zoom = dir === "down" ? view.w / (world.w * 1.06) : view.h / (world.h * 1.06);
  // Visible half-extent in world units at this zoom.
  const halfH = view.h / 2 / zoom;
  const halfW = view.w / 2 / zoom;
  if (dir === "down") {
    const top = world.y + halfH;
    const bot = world.y + world.h - halfH;
    return [
      { t: 0, cx: world.cx, cy: top, zoom },
      { t: dur, cx: world.cx, cy: Math.max(top, bot), zoom },
    ];
  }
  const left = world.x + halfW;
  const right = world.x + world.w - halfW;
  return [
    { t: 0, cx: left, cy: world.cy, zoom },
    { t: dur, cx: Math.max(left, right), cy: world.cy, zoom },
  ];
}
